import React, { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { Play, Pause } from 'lucide-react';

export default function Timeline({ 
  audioUrl, 
  lyrics, 
  setLyrics,
  currentTime, 
  setCurrentTime, 
  isPlaying, 
  setIsPlaying,
  duration,
  setDuration,
  wavesurferRef,
  audioContextRef,
  mediaElementSourceRef,
  audioDestRef,
  analyserRef,
  onPlayPause
}) {
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(50);
  const animationFrameRef = useRef();

  useEffect(() => {
    if (!containerRef.current || !audioUrl) return;

    // Initialize Web Audio Context for perfect programmatic sync
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioCtx;
    
    // We create a media element so Wavesurfer can use it, and we can hook it into Web Audio
    const audio = new Audio(audioUrl);
    audio.crossOrigin = "anonymous";
    
    // Set up web audio graph for export capability
    mediaElementSourceRef.current = audioCtx.createMediaElementSource(audio);
    audioDestRef.current = audioCtx.createMediaStreamDestination();
    
    // Set up AnalyserNode for visualizer
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    if (analyserRef) analyserRef.current = analyser;
    
    // Route audio through analyser to speakers AND to our destination node for export
    mediaElementSourceRef.current.connect(analyser);
    analyser.connect(audioCtx.destination);
    
    // Connect source directly to destination stream so export audio is clean
    mediaElementSourceRef.current.connect(audioDestRef.current);

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#4b5563',
      progressColor: '#10b981',
      cursorColor: '#ffffff',
      barWidth: 2,
      barGap: 1,
      height: 64,
      media: audio, // Use our audio element
      minPxPerSec: zoom,
    });
    
    wavesurferRef.current = ws;

    ws.on('ready', () => {
      setDuration(ws.getDuration());
    });

    ws.on('play', () => {
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      setIsPlaying(true);
    });
    
    ws.on('pause', () => setIsPlaying(false));
    
    ws.on('seek', () => {
      setCurrentTime(ws.getCurrentTime());
    });

    return () => {
      ws.destroy();
      if (audioCtx.state !== 'closed') {
        audioCtx.close();
      }
    };
  }, [audioUrl]);

  // Update zoom
  useEffect(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.zoom(zoom);
    }
  }, [zoom]);

  // High-precision clock using requestAnimationFrame synced to wavesurfer's current time
  const updateTime = useCallback(() => {
    if (wavesurferRef.current && isPlaying) {
      setCurrentTime(wavesurferRef.current.getCurrentTime());
      animationFrameRef.current = requestAnimationFrame(updateTime);
    }
  }, [isPlaying, setCurrentTime]);

  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateTime);
    } else {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isPlaying, updateTime]);

  // Drag and Resize state handled below

  // Drag and Resize state
  const draggingRef = useRef(null); // { id, type: 'move'|'resize', startX, initialStart, initialDuration }

  const handlePointerDown = (e, lyric, type) => {
    e.stopPropagation();
    draggingRef.current = {
      id: lyric.id,
      type,
      startX: e.clientX,
      initialStart: lyric.start,
      initialDuration: lyric.end - lyric.start
    };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  };

  const handlePointerMove = (e) => {
    if (!draggingRef.current) return;
    const { id, type, startX, initialStart, initialDuration } = draggingRef.current;
    
    // Calculate delta time
    const deltaX = e.clientX - startX;
    const deltaTime = deltaX / zoom;
    
    if (type === 'move') {
      const newStart = Math.max(0, initialStart + deltaTime);
      setLyrics(prev => prev.map(l => {
        if (l.id === id) {
          const newWords = l.words.map(w => {
            const wOffset = w.start - l.start;
            const wDur = w.end - w.start;
            return { ...w, start: newStart + wOffset, end: newStart + wOffset + wDur };
          });
          return { ...l, start: newStart, end: newStart + initialDuration, words: newWords };
        }
        return l;
      }));
    } else if (type === 'resize') {
      const newDuration = Math.max(0.5, initialDuration + deltaTime); // min duration 0.5s
      setLyrics(prev => prev.map(l => {
        if (l.id === id) {
          const scale = newDuration / initialDuration;
          const newWords = l.words.map(w => {
            const wOffset = w.start - l.start;
            const wDur = w.end - w.start;
            return { ...w, start: l.start + wOffset * scale, end: l.start + (wOffset + wDur) * scale };
          });
          return { ...l, end: l.start + newDuration, words: newWords };
        }
        return l;
      }));
    }
  };

  const handlePointerUp = () => {
    draggingRef.current = null;
    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerUp);
  };

  return (
    <div className="flex-1 flex flex-col p-4 relative">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={onPlayPause}
            className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center hover:bg-emerald-500"
            disabled={!audioUrl}
          >
            {isPlaying ? <Pause size={20} fill="currentColor"/> : <Play size={20} fill="currentColor" className="ml-1"/>}
          </button>
          <div className="text-xl font-mono text-gray-300">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400">Zoom</span>
          <input 
            type="range" min="10" max="200" 
            value={zoom} onChange={e => setZoom(Number(e.target.value))}
            className="w-32 accent-emerald-500"
            disabled={!audioUrl}
          />
        </div>
      </div>

      <div className="relative flex-1 bg-gray-900 border border-gray-800 rounded overflow-hidden flex flex-col">
        {/* Wavesurfer Container */}
        <div ref={containerRef} className="h-[64px] border-b border-gray-800 shrink-0" />
        
        {/* Lyric Timeline Track */}
        <div className="relative h-12 mt-2 bg-gray-800/50 overflow-hidden">
          {/* We create a scrollable container that syncs with wavesurfer's internal scroll.
              For simplicity in this POC, we'll absolutely position blocks based on pixels per second. 
              Wavesurfer scroll sync can be tricky, so we overlay it precisely or bind it to zoom. */}
          <div 
            className="absolute top-0 bottom-0 left-0 transition-transform duration-75"
            style={{ 
              width: `${duration * zoom}px`,
              // Attempt to sync visual translation with time (this is a simplified approach)
              transform: `translateX(calc(50vw - ${currentTime * zoom}px - 2rem))` 
            }}
          >
            {/* Note: the transform above keeps the playhead centered conceptually. 
                In a full app we'd sync perfectly to the WaveSurfer DOM wrapper's scrollLeft.
                Since wavesurfer.js manages its own scrolling, placing our track outside means we need to manually sync. */}
            
            {lyrics.map(lyric => (
              <div 
                key={lyric.id}
                onPointerDown={(e) => handlePointerDown(e, lyric, 'move')}
                className="absolute top-1 bottom-1 bg-emerald-600/80 border border-emerald-400 rounded px-2 text-xs flex items-center overflow-hidden cursor-move hover:bg-emerald-500/80 group"
                style={{
                  left: `${lyric.start * zoom}px`,
                  width: `${(lyric.end - lyric.start) * zoom}px`
                }}
                title={lyric.text}
              >
                <span className="truncate w-full select-none">{lyric.text}</span>
                <div 
                  className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize bg-emerald-400 opacity-0 group-hover:opacity-100 hover:w-3 transition-all"
                  onPointerDown={(e) => handlePointerDown(e, lyric, 'resize')}
                />
              </div>
            ))}
          </div>
          
          {/* Static Playhead line for UI feedback */}
          <div className="absolute top-0 bottom-0 left-[calc(50vw-2rem)] w-[2px] bg-red-500 z-10 shadow-[0_0_10px_red]"></div>
        </div>
        {!audioUrl && <div className="absolute inset-0 flex items-center justify-center text-gray-500">Import audio to start</div>}
      </div>
    </div>
  );
}

function formatTime(secs) {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  const ms = Math.floor((secs % 1) * 100);
  return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
}